import React, { useState, useRef, useCallback } from 'react';
import { 
  Camera, X, Image, Loader2, ShoppingCart, Edit, Search, 
  RefreshCw, Upload, CheckCircle, AlertCircle, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Flow states
const FLOW_STATE = {
  CAPTURE: 'capture',
  ANALYZING: 'analyzing',
  FOUND: 'found',
  NOT_FOUND: 'not_found',
  CONFIRM_ACTION: 'confirm_action',
};

const PictureSearch = ({ 
  isOpen, 
  onClose, 
  mode = 'cart', // 'cart' or 'edit'
  onAddToCart,
  onEditProduct,
  products = []
}) => {
  const [capturedImage, setCapturedImage] = useState(null);
  const [flowState, setFlowState] = useState(FLOW_STATE.CAPTURE);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [aiInfo, setAiInfo] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Start camera
  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(
        err.name === 'NotAllowedError' 
          ? 'Camera permission denied. Please allow camera access.'
          : 'Could not access camera. Try uploading an image instead.'
      );
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageData);
    stopCamera();
    
    // Automatically analyze
    analyzeImage(imageData);
  }, [stopCamera]);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = e.target.result;
      setCapturedImage(imageData);
      stopCamera();
      // Automatically analyze
      analyzeImage(imageData);
    };
    reader.readAsDataURL(file);
  };

  // Analyze image with AI
  const analyzeImage = async (imageData) => {
    if (!imageData) return;
    
    setFlowState(FLOW_STATE.ANALYZING);
    setSearchResults([]);
    setSelectedProduct(null);
    setAiInfo(null);
    
    try {
      const response = await fetch(`${API_URL}/api/products/image-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: imageData,
          mode: mode
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to analyze image');
      }
      
      const data = await response.json();
      
      const results = data.products || [];
      setSearchResults(results);
      setAiInfo({
        description: data.ai_description,
        product: data.identified_product,
        brand: data.identified_brand,
        category: data.identified_category,
        confidence: data.confidence,
        searchTerms: data.search_terms
      });
      
      if (results.length > 0) {
        // Automatically select the first result
        setSelectedProduct(results[0]);
        setFlowState(FLOW_STATE.FOUND);
      } else {
        setFlowState(FLOW_STATE.NOT_FOUND);
      }
      
    } catch (err) {
      console.error('Analysis error:', err);
      toast.error(err.message || 'Failed to analyze image');
      setFlowState(FLOW_STATE.NOT_FOUND);
    }
  };

  // Reset to take new photo
  const resetCapture = () => {
    setCapturedImage(null);
    setSearchResults([]);
    setSelectedProduct(null);
    setAiInfo(null);
    setFlowState(FLOW_STATE.CAPTURE);
    startCamera();
  };

  // Handle close
  const handleClose = () => {
    stopCamera();
    setCapturedImage(null);
    setSearchResults([]);
    setSelectedProduct(null);
    setAiInfo(null);
    setFlowState(FLOW_STATE.CAPTURE);
    onClose();
  };

  // Handle "Yes, Add to Cart"
  const handleAddToCart = () => {
    if (selectedProduct && onAddToCart) {
      onAddToCart(selectedProduct);
      toast.success(`Added ${selectedProduct.name} to cart`);
    }
    // Show "Take another picture?" prompt
    setFlowState(FLOW_STATE.CONFIRM_ACTION);
  };

  // Handle "Yes, Edit"
  const handleEditProduct = () => {
    if (selectedProduct && onEditProduct) {
      onEditProduct(selectedProduct);
      handleClose();
    }
  };

  // Handle "No" - go to take another prompt
  const handleDecline = () => {
    setFlowState(FLOW_STATE.CONFIRM_ACTION);
  };

  // Handle selecting a different product from results
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
  };

  // Handle "Take Another Picture"
  const handleTakeAnother = () => {
    resetCapture();
  };

  // Start camera when opened
  React.useEffect(() => {
    if (isOpen && flowState === FLOW_STATE.CAPTURE && !capturedImage) {
      startCamera();
    }
    return () => stopCamera();
  }, [isOpen, flowState, capturedImage, startCamera, stopCamera]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black" data-testid="picture-search">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">
                {mode === 'cart' ? 'PICTURES2CART' : 'PICTURE2EDIT'}
              </h2>
              <p className="text-white/70 text-sm">
                {flowState === FLOW_STATE.CAPTURE ? 'Take a picture of any product' : 
                 flowState === FLOW_STATE.ANALYZING ? 'AI is analyzing...' : 
                 'Product found!'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-white hover:bg-white/20"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Camera View / Captured Image */}
      <div className="absolute inset-0 flex items-center justify-center">
        {flowState === FLOW_STATE.CAPTURE && !capturedImage ? (
          <>
            {/* Live Camera Feed */}
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              muted
            />
            <canvas ref={canvasRef} className="hidden" />
            
            {/* Camera Guide */}
            {cameraActive && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="relative w-72 h-72 border-2 border-white/50 rounded-2xl">
                  <div className="absolute -top-1 -left-1 w-10 h-10 border-t-4 border-l-4 border-purple-400 rounded-tl-2xl" />
                  <div className="absolute -top-1 -right-1 w-10 h-10 border-t-4 border-r-4 border-purple-400 rounded-tr-2xl" />
                  <div className="absolute -bottom-1 -left-1 w-10 h-10 border-b-4 border-l-4 border-purple-400 rounded-bl-2xl" />
                  <div className="absolute -bottom-1 -right-1 w-10 h-10 border-b-4 border-r-4 border-purple-400 rounded-br-2xl" />
                </div>
                <p className="absolute bottom-32 text-white/80 text-sm bg-black/50 px-4 py-2 rounded-full">
                  Position product in frame
                </p>
              </div>
            )}
            
            {/* Camera Error */}
            {cameraError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/90 p-4">
                <Card className="p-6 max-w-sm w-full text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <p className="text-gray-800 mb-4">{cameraError}</p>
                  <div className="space-y-2">
                    <Button onClick={startCamera} className="w-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image Instead
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </>
        ) : (
          /* Captured Image Preview */
          <img
            src={capturedImage}
            alt="Captured"
            className="max-w-full max-h-full object-contain"
          />
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20">
        
        {/* Analyzing State */}
        {flowState === FLOW_STATE.ANALYZING && (
          <div className="bg-white rounded-t-3xl p-8 text-center animate-slide-up">
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
            <h3 className="font-bold text-gray-800 text-lg mb-2">Analyzing Image...</h3>
            <p className="text-gray-500">AI is identifying the product</p>
          </div>
        )}

        {/* Product Found - Ask to Add/Edit */}
        {flowState === FLOW_STATE.FOUND && selectedProduct && (
          <div className="bg-white rounded-t-3xl shadow-2xl animate-slide-up">
            {/* AI Info */}
            {aiInfo && (
              <div className="p-4 bg-purple-50 border-b border-purple-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800 text-sm">AI Identified:</span>
                      <Badge className={`text-xs ${
                        aiInfo.confidence === 'high' ? 'bg-green-100 text-green-700' :
                        aiInfo.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {aiInfo.confidence} confidence
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm">{aiInfo.product || aiInfo.description}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-6">
              {/* Success indicator */}
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
              </div>
              
              {/* Selected Product info */}
              <div className="flex items-center gap-4 mb-4 p-4 bg-gray-50 rounded-xl">
                <img 
                  src={selectedProduct.image} 
                  alt={selectedProduct.name}
                  className="w-20 h-20 rounded-xl object-cover"
                />
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800 text-lg">{selectedProduct.name}</h3>
                  <p className="text-green-600 font-bold text-xl">${selectedProduct.price?.toFixed(2)}</p>
                  <Badge variant="outline" className="mt-1">{selectedProduct.category}</Badge>
                </div>
              </div>

              {/* Other matches - show if more than 1 result */}
              {searchResults.length > 1 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Other matches:</p>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {searchResults.filter(p => p.id !== selectedProduct.id).slice(0, 4).map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleSelectProduct(product)}
                        className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-400 transition-all"
                      >
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Action prompt */}
              <p className="text-center text-gray-700 font-medium mb-4">
                {mode === 'cart' ? 'Add this product to cart?' : 'Edit this product?'}
              </p>

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={handleDecline}
                  className="flex-1 py-6"
                >
                  No
                </Button>
                <Button 
                  onClick={mode === 'cart' ? handleAddToCart : handleEditProduct}
                  className={`flex-1 py-6 ${mode === 'cart' ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'}`}
                >
                  {mode === 'cart' ? (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Yes, Add to Cart
                    </>
                  ) : (
                    <>
                      <Edit className="w-5 h-5 mr-2" />
                      Yes, Edit
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Not Found */}
        {flowState === FLOW_STATE.NOT_FOUND && (
          <div className="bg-white rounded-t-3xl shadow-2xl animate-slide-up">
            <div className="p-6">
              {/* Warning indicator */}
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <AlertCircle className="w-7 h-7 text-yellow-600" />
                </div>
              </div>

              <h3 className="text-center font-bold text-gray-800 text-lg mb-2">No Products Found</h3>
              <p className="text-center text-gray-500 mb-6">
                We could not find a matching product. Try taking a clearer picture or from a different angle.
              </p>

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={handleClose}
                  className="flex-1 py-6"
                >
                  Exit
                </Button>
                <Button 
                  onClick={handleTakeAnother}
                  className="flex-1 py-6 bg-purple-500 hover:bg-purple-600"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Action - Take Another or Exit */}
        {flowState === FLOW_STATE.CONFIRM_ACTION && (
          <div className="bg-white rounded-t-3xl shadow-2xl animate-slide-up">
            <div className="p-6">
              {/* Question indicator */}
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Camera className="w-7 h-7 text-purple-600" />
                </div>
              </div>

              <h3 className="text-center font-bold text-gray-800 text-lg mb-2">What would you like to do?</h3>
              <p className="text-center text-gray-500 mb-6">Take another picture or exit?</p>

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={handleClose}
                  className="flex-1 py-6"
                >
                  <X className="w-5 h-5 mr-2" />
                  Exit
                </Button>
                <Button 
                  onClick={handleTakeAnother}
                  className="flex-1 py-6 bg-purple-500 hover:bg-purple-600"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Take Another Picture
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Capture Controls - Only when in capture mode */}
        {flowState === FLOW_STATE.CAPTURE && !cameraError && (
          <div className="bg-gradient-to-t from-black via-black/90 to-transparent p-6 pt-12">
            <div className="flex items-center justify-center gap-6">
              <Button
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                className="w-14 h-14 rounded-full border-2 border-white/30 bg-white/10 hover:bg-white/20"
              >
                <Image className="w-6 h-6 text-white" />
              </Button>
              
              <Button
                onClick={capturePhoto}
                disabled={!cameraActive}
                className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-lg"
              >
                <Camera className="w-8 h-8 text-white" />
              </Button>
              
              <div className="w-14 h-14" /> {/* Spacer for alignment */}
            </div>
          </div>
        )}
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PictureSearch;
